import { IcEdit, IcPlus as IcPlus24, IcTrash } from '@assets/icons/24x24';
import { IcPlus as IcPlus32 } from '@assets/icons/32x32';
import { ButtonGroup, For, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import CockpitRow from '@components/cockpit/layouts/CockpitRow';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { hasRole, selectDenormalizedUsers } from '@redux/authent/authent.selectors';
import { useDeleteUserMutation } from '@services/authent/user.api';
import { isRestrictedUser } from '@utils/authent/profiles.utils';
import { getFirstNameAndName } from '@utils/authent/users.utils';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { User } from '@/types/authent/login.types';
import { RoleEnum } from '@/types/authent/roles.types';

import { UsersManagementItemTypeEnum, useUsersManagementContext } from '../UsersManagement';

type UsersDialogProps = {
  name: string;
} & Omit<CockpitDialogProps, 'children'>;

function UsersDialog({ name, open, isLoading, onConfirm, onCancel }: UsersDialogProps) {
  return (
    <CockpitDialog open={open} isLoading={isLoading} onCancel={onCancel} onConfirm={onConfirm}>
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage id="components.usersManagement.users.popup.descriptionDelete" values={{ name: name }} />
      </Text>
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage id="components.usersManagement.users.popup.descriptionDeleteLogout" />
      </Text>
    </CockpitDialog>
  );
}

type UsersActionsProps = {
  canCreateUser: boolean;
  usersLength: number;
  onCreate: () => void;
};

function UsersActions({ canCreateUser, usersLength, onCreate }: Readonly<UsersActionsProps>) {
  return (
    <>
      <Text color="neutral.500">
        <FormattedMessage id="components.usersManagement.users.result" values={{ count: usersLength }} />
      </Text>
      {canCreateUser && (
        <IconButton aria-label="Add" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onCreate}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

export default function Users() {
  const { formatMessage } = useIntl();
  const users = useAppSelector(selectDenormalizedUsers);
  const canEditUser = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_USER));
  const canCreateUser = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_USER));
  const canDeleteUser = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_USER));
  const [deleteUser, { isLoading }] = useDeleteUserMutation();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const { setEditedItem } = useUsersManagementContext();

  return (
    <CockpitListLayout
      dialog={
        <UsersDialog
          open={selectedUser !== null}
          isLoading={isLoading}
          onCancel={() => setSelectedUser(null)}
          onConfirm={() => {
            if (!selectedUser) {
              return;
            }
            deleteUser(selectedUser.id)
              .unwrap()
              .then(() => {
                createToast(
                  formatMessage(
                    { id: 'components.usersManagement.users.message.delete' },
                    { name: `${selectedUser.firstName} ${selectedUser.name}` },
                  ),
                  ToastStatusEnum.SUCCESS,
                );
                setSelectedUser(null);
              });
          }}
          name={selectedUser ? `${selectedUser.firstName} ${selectedUser.name}` : ''}
        />
      }
      actions={
        <UsersActions
          canCreateUser={canCreateUser}
          usersLength={users.length}
          onCreate={() => setEditedItem({ type: UsersManagementItemTypeEnum.USER, id: null })}
        />
      }
    >
      <For
        each={users.toSorted((a, b) => getFirstNameAndName(a).localeCompare(getFirstNameAndName(b)))}
        fallback={
          canCreateUser ? (
            <VStack
              padding={4}
              gap={2}
              marginY="auto"
              className="group"
              border="2px solid"
              cursor="pointer"
              borderColor="neutral.600"
              _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
              onClick={() => setEditedItem({ type: UsersManagementItemTypeEnum.USER, id: null })}
            >
              <Icon as={IcPlus32} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              <Text>
                <FormattedMessage id="components.usersManagement.users.createFirst" />
              </Text>
            </VStack>
          ) : undefined
        }
      >
        {(user, index) => {
          const isReadonly = isRestrictedUser(user);
          return (
            <CockpitRow index={index} listLength={users.length} key={index}>
              <HStack gap={0.5}>
                <Text>{user.firstName}</Text>
                <Text>{user.name}</Text>
              </HStack>
              {!isReadonly && (
                <ButtonGroup marginLeft="auto" attached height="100%">
                  {canEditUser && (
                    <IconButton
                      variant="transparent"
                      boxSize="56px"
                      aria-label="edit"
                      onClick={() => setEditedItem({ type: UsersManagementItemTypeEnum.USER, id: user.id })}
                    >
                      <Icon as={IcEdit} boxSize="24px" />
                    </IconButton>
                  )}
                  {canDeleteUser && (
                    <IconButton
                      variant="transparent"
                      boxSize="56px"
                      aria-label="delete"
                      onClick={() => {
                        setSelectedUser(user);
                      }}
                    >
                      <Icon as={IcTrash} boxSize="24px" />
                    </IconButton>
                  )}
                </ButtonGroup>
              )}
            </CockpitRow>
          );
        }}
      </For>
    </CockpitListLayout>
  );
}
